import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import classNames from "classnames";
import {useCallback, useEffect, useRef, useState} from "react";
import {DateRange, TZDate} from "react-day-picker";
import {FiCalendar} from "react-icons/fi";

import {Button} from "@/components/button/Button.tsx";
import {DatePickerMode, DatePickerV3Props, DatePickerValue} from "@/components/datePicker/DatePicker.types.ts";
import {
  convertIncomingToTZDate,
  dateToInputValue,
  selectedRangeUseEffectDependency,
} from "@/components/datePicker/DatePicker.utils.ts";
import {MemoizedDatePickerCalendar} from "@/components/datePicker/subComponents/DatePickerCalendar/DatePickerCalendarMemoized.tsx";
import {DatePickerInput} from "@/components/datePicker/subComponents/DatePickerInput/DatePickerInput.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {StandardDateRange} from "@/lib/queries/reports.ts";
import {DateFormatName} from "@/lib/types/common.ts";
import {StandardDateRangeLabels} from "@/lib/types/reports.ts";

import styles from "./DatePicker.module.scss";

export const DatePicker = <Mode extends DatePickerMode = "range">({
  mode = "range" as Mode,
  placement = "bottom",
  dateFormat = DateFormatName.DEFAULT,
  timeFormat,
  onChange,
  selected: selectedProp,
  splitRole,
  fixedRange: fixedRangeProp,
  resetDefault,
  inputProps,
  className,
  timezone,
  name = "date-picker",
  placeholder,
  disabled,
  readOnly,
  ...rest
}: DatePickerV3Props<Mode>) => {
  const [error, setError] = useState<string | null>(null);
  const [show, setShow] = useState(false);
  const [theme, setTheme] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [prevSelected, setPrevSelected] = useState<DatePickerValue<Mode>>(
    convertIncomingToTZDate(selectedProp, timezone) as DatePickerValue<Mode>
  );
  const [selected, setSelected] = useState<DatePickerValue<Mode>>(
    convertIncomingToTZDate(selectedProp, timezone) as DatePickerValue<Mode>
  );

  const [fixedRange, setFixedRange] = useState<StandardDateRange | undefined>(fixedRangeProp);
  const [prevFixedRange, setPrevFixedRange] = useState<StandardDateRange | undefined>(fixedRangeProp);

  const [inputValue, setInputValue] = useState<string>("");

  //Functions to update selected and fixedRange state and inputValue
  const setSelectedFixedRangeAndSetInputValue = useCallback(
    (date: DatePickerValue<Mode>, fixedRange: StandardDateRange | undefined) => {
      setSelected(date);
      setFixedRange(fixedRange);
      setInputValue(dateToInputValue(date, dateFormat, timeFormat, mode, timezone, splitRole));
      setError(null);
    },
    [dateFormat, timeFormat, mode, timezone, splitRole]
  );

  const setSelectedAndSetInputValue = useCallback(
    (date: DatePickerValue<Mode>) => {
      setSelected(date);
      setInputValue(dateToInputValue(date, dateFormat, timeFormat, mode, timezone, splitRole));
      setError(null);
    },
    [dateFormat, timeFormat, mode, timezone, splitRole]
  );

  // Memoized functions for the Calendar component
  const onHide = useCallback(() => {
    setShow(false);
    setSelected(prevSelected);
    setFixedRange(prevFixedRange);
    setInputValue(dateToInputValue(prevSelected, dateFormat, timeFormat, mode, timezone, splitRole));
    setError(null);
  }, [prevSelected, prevFixedRange, dateFormat, timeFormat, mode, timezone, splitRole]);

  // Commit the current selection when the picker is dismissed by clicking outside
  // or via Done. An incomplete range (only one endpoint chosen) reverts to
  // the previously committed value rather than applying a half-range; a fully
  // cleared value commits as empty, so clearing the input sticks across every mode.
  const onCommit = useCallback(() => {
    setError(null);
    const date = selected;
    if (mode === "range" && date && "to" in date) {
      if (!date.from || !date.to || date.from > date.to) {
        onHide();
        return;
      }
    }
    setPrevSelected(date);
    setPrevFixedRange(fixedRange);
    setSelectedFixedRangeAndSetInputValue(date, fixedRange);
    onChange?.(date, fixedRange);
    setShow(false);
  }, [mode, selected, fixedRange, onChange, onHide, setSelectedFixedRangeAndSetInputValue]);

  // Reset sets the selection to `resetDefault` (or clears it when none is given)
  // and commits it, leaving the picker open. Reverting to the value the picker
  // opened with is the Cancel button's (and Escape's) job, not Reset's.
  const onResetHandler = useCallback(() => {
    let resetValue = convertIncomingToTZDate(resetDefault, timezone) as DatePickerValue<Mode>;
    // In split mode this picker owns only one endpoint of a shared range; clearing
    // must preserve the other endpoint so it isn't wiped (and so internal state
    // stays in sync with the value the consumer keeps).
    if (mode === "split" && !resetDefault) {
      const current = selected as DateRange | undefined;
      resetValue = {
        from: splitRole === "start" ? undefined : current?.from,
        to: splitRole === "end" ? undefined : current?.to,
      } as DatePickerValue<Mode>;
    }
    setPrevSelected(resetValue);
    setPrevFixedRange(undefined);
    setSelectedFixedRangeAndSetInputValue(resetValue, undefined);
    onChange?.(resetValue, undefined);
  }, [mode, splitRole, resetDefault, selected, timezone, onChange, setSelectedFixedRangeAndSetInputValue]);

  const onChangeHandler = useCallback(
    (date: Date | DateRange | TZDate | undefined, fixedRange: StandardDateRange | undefined) => {
      setSelectedFixedRangeAndSetInputValue(date as DatePickerValue<Mode>, fixedRange);
    },
    [setSelectedFixedRangeAndSetInputValue]
  );

  // Convert the initial selectedProp to a DatePickerValue and set it as the initial state
  useEffect(() => {
    const convertedSelected = convertIncomingToTZDate(selectedProp, timezone) as DatePickerValue<Mode>;
    setPrevSelected(convertedSelected);
    setSelectedAndSetInputValue(convertedSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...selectedRangeUseEffectDependency(selectedProp), setSelectedAndSetInputValue, timezone]);

  // The owner can resolve its range after mount (status page settings, URL params), and `selected`
  // alone does not say which shortcut produced it — without this the readOnly label and the
  // highlighted shortcut keep naming the mount-time range while the page shows a different one.
  useEffect(() => {
    setFixedRange(fixedRangeProp);
    setPrevFixedRange(fixedRangeProp);
  }, [fixedRangeProp]);

  const {refs, floatingStyles, context} = useFloating({
    placement: placement,
    open: show,
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, {
        ancestorScroll: true,
        ancestorResize: true,
        elementResize: true,
        layoutShift: true,
      }),
    onOpenChange(nextOpen, _, reason) {
      setShow(nextOpen);
      if (reason === "escape-key") {
        onHide();
      } else if (reason === "outside-press") {
        onCommit();
      }
    },
    middleware: [
      offset(8),
      shift({
        padding: 4,
      }),
      flip({}),
    ],
  });

  const dismiss = useDismiss(context, {
    referencePress: false,
  });
  const role = useRole(context);

  const {getReferenceProps, getFloatingProps} = useInteractions([role, dismiss]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const themeRoot = el.closest("[data-theme]");
    if (!themeRoot) return;

    setTheme(themeRoot.getAttribute("data-theme"));
    const observer = new MutationObserver(() => {
      setTheme(themeRoot.getAttribute("data-theme"));
    });
    observer.observe(themeRoot, {attributes: true, attributeFilter: ["data-theme"]});
    return () => observer.disconnect();
  }, []);

  const readOnlyLabel = readOnly
    ? (fixedRange ? (StandardDateRangeLabels[fixedRange] ?? inputValue) : inputValue) || placeholder || ""
    : undefined;

  return (
    <div ref={wrapperRef}>
      {readOnly ? (
        <Button
          variant="inputField"
          size="small"
          ref={refs.setReference}
          {...getReferenceProps()}
          onClick={() => setShow((v) => !v)}
        >
          <span className={styles.readOnlyLabel}>{readOnlyLabel}</span>
          <Icon icon={FiCalendar} className={styles.calendarIcon} removeDivDimensions />
        </Button>
      ) : (
        <DatePickerInput
          {...inputProps}
          {...getReferenceProps()}
          name={name}
          timezone={timezone}
          className={classNames(styles.datePickerInput, className)}
          value={inputValue}
          ref={refs.setReference}
          mode={mode}
          currentDate={selected ? selected : undefined}
          splitRole={splitRole}
          placeholder={placeholder}
          disabled={disabled}
          error={error}
          setError={setError}
          onChange={(_, value, date) => {
            setInputValue(value);
            setSelected(date as DatePickerValue<Mode>);
            setFixedRange(undefined);
          }}
          onMouseDown={() => {
            setShow(true);
          }}
        />
      )}
      {show && (
        <FloatingPortal>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div
            className={styles.datePickerPopover}
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            {...(theme ? {"data-theme": theme} : {})}
          >
            <div className={"border-1 border-color-gray-300 d-flex"}>
              <MemoizedDatePickerCalendar
                name={name}
                mode={mode}
                selected={selected}
                splitRole={splitRole}
                fixedRange={fixedRange}
                onCancel={onHide}
                onReset={onResetHandler}
                onDone={onCommit}
                onChange={onChangeHandler}
                dateFormat={dateFormat}
                timeFormat={timeFormat}
                timezone={timezone}
                {...rest}
              />
            </div>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};
